import React, { Component } from 'react'
import { connect } from 'react-redux'
import deepEqual from 'deep-equal'

import {
  COMPONENT,
  LABEL,
  SHAPE,
  SECTION,
  ELLIPSE,
  INPUT,
  inputTypeOptions,
  IMAGE_UPLOAD,
  FILE_UPLOAD,
  DATE_PICKER,
  WEB_VIEW,
  SELECT,
  IMAGE,
  dataTypes,
  GROUP,
  LIST,
  LINE,
  LOCATION_INPUT,
} from '@adalo/constants'

import GoogleHelpLink from 'components/Shared/GoogleHelpLink'
import { getThirdPartyApiKeyFor } from 'ducks/thirdPartyApiKeys'
import PrerequisiteAlert from 'components/Shared/PrerequisiteAlert'

import {
  selectObjects,
  updateObjects,
  updateObject,
  getComponentId,
  getCurrentAppId,
} from '../../../ducks/editor/objects'

import { getFieldIds } from '../../../utils/fields'
import {
  getDateTimeSources,
  getBindingSuggestions,
} from '../../../ducks/recommender'
import { getSelection } from '../../../ducks/editor/selection'
import { PanelSection } from '../../Shared/Panel'
import StylesAccordion from '../../Shared/StylesAccordion'
import EmptyState from '../../Shared/EmptyState'
import BindableTextControl from '../../Shared/BindableTextControl'
import NewActionRow from '../Actions/Row'
import InspectBody from './Body'
import BackgroundControl from './BackgroundControl'
import BorderControl from './BorderControl'
import BorderRadiusControl, { getMaxBorderRadius } from './BorderRadiusControl'
import PropControl from './Libraries/PropControl'
import MenuControl from './Libraries/MenuControl'
import GenericInspectRow from './GenericRow'
import ListControl from './ListControl'
import SelectLabelControl from './SelectLabelControl'
import SelectDefaultValueControl from './SelectDefaultValueControl'
import MultiSelectionControl from './MultiSelectionControl'
import GroupControl from './GroupControl'
import IconRow from './Libraries/IconRow'
import LocationControl from './Libraries/LocationControl'

import './Inspect.css'

const STYLES_ACCORDION = new Set([
  COMPONENT,
  LABEL,
  SHAPE,
  SECTION,
  ELLIPSE,
  INPUT,
  IMAGE_UPLOAD,
  FILE_UPLOAD,
  DATE_PICKER,
  WEB_VIEW,
  SELECT,
  IMAGE,
  GROUP,
  LIST,
  LINE,
  LOCATION_INPUT,
])

class InspectMenu extends Component {
  handleChange = value => {
    const { objects, updateObjects } = this.props

    const newObjects = objects.map(({ id }) => ({ ...value, id }))
    updateObjects(newObjects)
  }

  handleComposedValueChange = newValue => {
    const { composedValue: value } = newValue

    if (value === 'uploaded') {
      this.handleChange({ imageType: 'uploaded', imageBinding: null })
    } else if (value === 'url') {
      this.handleChange({ imageType: 'url', imageBinding: null })
    } else if (typeof value !== 'undefined') {
      this.handleChange({
        imageType: 'dynamic',
        imageBinding: value,
        filename1x: null,
      })
    } else {
      this.handleChange(newValue)
    }
  }

  getFieldOptions(includeNone = false) {
    const { fields } = this.props

    const simpleTypes = [
      dataTypes.TEXT,
      dataTypes.NUMBER,
      dataTypes.DATE,
      dataTypes.BOOLEAN,
    ]

    const options = []

    if (includeNone) {
      options.push({ label: 'None', value: null })
      options.push(null)
    }

    for (const fieldId of getFieldIds(fields)) {
      const field = fields[fieldId]

      if (simpleTypes.indexOf(field.type) !== -1) {
        options.push({
          label: field.name,
          value: fieldId,
        })
      }
    }

    return options
  }

  render() {
    const { appId, componentId, objects, googleApiKeyIsValid } = this.props

    if (!objects || objects.length === 0) {
      return <EmptyState>Nothing Selected</EmptyState>
    }

    const object = objects.length === 1 ? objects[0] : null

    // truthy if there are multiple objects and object is null
    // or if there is a single object and it's in STYLES_ACCORDION
    if (objects.length > 1 || (object && STYLES_ACCORDION.has(object.type))) {
      const result = []

      if (object && new Set([SHAPE, SECTION, LINE, ELLIPSE]).has(object.type)) {
        if (new Set([SHAPE, SECTION, ELLIPSE]).has(object.type)) {
          result.push(
            <BackgroundControl
              key="background-control"
              appId={appId}
              object={object}
            />
          )
        }

        result.push(
          <BorderControl key="border-control" appId={appId} object={object} />
        )
      }

      if (object && object.type === SECTION) {
        result.push(
          <BorderRadiusControl
            key="border-radius-control"
            onChange={this.handleChange}
            value={object.borderRadius}
            name="borderRadius"
            min={0}
            max={getMaxBorderRadius(object)}
          />
        )
      }

      if (object && object.type === LABEL) {
        const prop = {
          name: 'text',
          displayName: 'Text',
          type: 'text',
        }

        result.push(
          <GenericInspectRow
            key="dropdownOptions"
            title=""
            className="default-date-control"
          >
            <PropControl
              objectId={object.id}
              onChange={this.handleChange}
              value={object.text}
              prop={prop}
              // NOTE: this is not actually an aria role, eslint is confused
              // eslint-disable-next-line jsx-a11y/aria-role
              role="text"
              reference="text"
            />
          </GenericInspectRow>
        )
      }

      if (object && object.type === WEB_VIEW) {
        result.push(
          <BindableTextControl
            key="uri"
            displayName="URL"
            name="uri"
            placeholder="https://example.com"
            value={object.uri}
            objectId={object.id}
            onChange={this.handleChange}
          />
        )
      }

      if (object && object.type === DATE_PICKER) {
        const options = getDateTimeSources([dataTypes.DATE]).children

        result.push(
          <MenuControl
            key="defaultDate"
            name="defaultDate"
            displayName="Default Date"
            value={object.defaultDate || options[0].value}
            onChange={this.handleChange}
            options={options}
            comparator={deepEqual}
          />
        )

        result.push(
          <MenuControl
            key="datePickerStyle"
            name="datePickerStyle"
            displayName="Style"
            value={object.datePickerStyle || dataTypes.DATE}
            onChange={this.handleChange}
            options={[
              { label: 'Date & Time Picker', value: dataTypes.DATE },
              { label: 'Date Picker', value: dataTypes.DATE_ONLY },
              { label: 'Date Text Input', value: 'dateTextInput' },
            ]}
          />
        )
      }

      if (object && object.type === SELECT) {
        const prop = {
          type: SELECT,
          name: 'select',
          value: object.select,
          objectId: object.id,
        }

        result.push(
          <BindableTextControl
            key="placeholder"
            disableBinding
            hideBinding
            displayName="Placeholder"
            name="placeholder"
            value={object.placeholder}
            objectId={object.id}
            onChange={this.handleChange}
          />
        )

        result.push(
          <GenericInspectRow
            key="dropdownOptions"
            title=""
            className="default-select-control"
          >
            <PropControl
              objectId={object.id}
              onChange={this.handleChange}
              value={object.select}
              prop={prop}
            />
            {object.select ? (
              <>
                <SelectLabelControl
                  appId={appId}
                  value={object.select}
                  onChange={this.handleChange}
                  name="select"
                />
                <SelectDefaultValueControl
                  appId={appId}
                  componentId={componentId}
                  objectId={object.id}
                  value={object?.defaultValue}
                  onChange={this.handleChange}
                />
              </>
            ) : null}
          </GenericInspectRow>
        )
      }

      if (object && object.type === INPUT) {
        result.push(
          <MenuControl
            autoSelect
            key="inputType"
            name="inputType"
            displayName="Type"
            options={inputTypeOptions}
            value={object.inputType}
            onChange={this.handleChange}
          />
        )

        result.push(
          <BindableTextControl
            key="placeholder"
            disableBinding
            hideBinding
            displayName="Placeholder"
            name="placeholder"
            value={object.placeholder}
            objectId={object.id}
            onChange={this.handleChange}
          />
        )

        result.push(
          <BindableTextControl
            key="defaultValue"
            displayName="Default Value"
            name="defaultValue"
            value={object.defaultValue}
            objectId={object.id}
            onChange={this.handleChange}
          />
        )
      }

      if (object && object.type === IMAGE) {
        let imageType = object.imageType

        if (!imageType && typeof object.filename1x === 'string') {
          imageType = 'uploaded'
        }

        const value =
          object.imageType === 'dynamic' ? object.imageBinding : imageType

        result.push(
          <PropControl
            key="imageControl"
            canBeUploaded
            objectId={object.id}
            appId={appId}
            onChange={this.handleComposedValueChange}
            value={value}
            prop={{
              type: 'image',
              name: 'composedValue',
              displayName: 'Image Source',
            }}
          />
        )
      }

      if (object && object.type === GROUP) {
        result.push(<GroupControl object={object} key="group-control" />)
      }

      if (object && object.type === LIST) {
        result.push(
          <ListControl
            key="list-control"
            appId={appId}
            componentId={componentId}
            object={object}
            onChange={this.handleChange}
          />
        )
      }

      if (object && object.type === LOCATION_INPUT) {
        result.push(
          <PrerequisiteAlert
            isPrerequisiteMet={googleApiKeyIsValid}
            name="Google Maps API Key"
            footer={<GoogleHelpLink />}
            context="component"
            key="googleApiKey"
          />
        )

        result.push(
          <BindableTextControl
            key="placeholder"
            disableBinding
            hideBinding
            displayName="Placeholder"
            name="placeholder"
            value={object.placeholder}
            objectId={object.id}
            onChange={this.handleChange}
          />
        )

        result.push(
          <IconRow
            key="icon"
            displayName="Icon"
            name="icon"
            value={object.icon}
            onChange={this.handleChange}
          />
        )

        result.push(
          <LocationControl
            key="defaultLocation"
            onChange={this.handleChange}
            defaultValue={object.defaultValue}
            objectId={object.id}
          />
        )
      }

      if (
        object &&
        new Set([SHAPE, SECTION, ELLIPSE, LABEL, IMAGE, GROUP]).has(object.type)
      ) {
        result.push(
          <NewActionRow
            key="click-actions"
            displayName="Click Actions"
            appId={appId}
            componentId={componentId}
            object={object}
          />
        )
      }

      if (objects.length > 1) {
        result.push(
          <MultiSelectionControl
            objects={objects}
            key="multi-selection-control"
          />
        )
      }

      result.push(
        <StylesAccordion
          spaced
          key="styles"
          renderChildren={() => <InspectBody objects={objects} />}
        />
      )

      return result
    }

    return (
      <PanelSection title="Properties">
        <InspectBody objects={objects} />
      </PanelSection>
    )
  }
}

const mapStateToProps = (state, props) => {
  const appId = getCurrentAppId(state)
  const selection = getSelection(state)
  const objects = selectObjects(state, selection)
  const googleApiKey = getThirdPartyApiKeyFor(state, appId, 'google')
  const googleApiKeyIsValid = googleApiKey && googleApiKey.isValid
  let object = {}
  let componentId = null

  if (selection.length === 0) return { appId }

  if (selection.length === 1) {
    componentId = getComponentId(state, selection[0])
    object = objects[0]
  }

  return {
    appId,
    componentId,
    objects,
    googleApiKeyIsValid,
    bindingOptions: object
      ? getBindingSuggestions(
          state,
          appId,
          componentId,
          object.id,
          true,
          'libraryProp'
        )()
      : null,
  }
}

export default connect(mapStateToProps, { updateObjects, updateObject })(
  InspectMenu
)
